import { useCallback, useMemo, useState } from 'react';
import { StanButton } from '@/components';
import { useChallenge, useUser } from '@/queries';
import { mainApi } from '@/lib/api';
import { cn } from '@/lib/utils/cn';
import { ChallengeCountdown } from './ChallengeCountdown';
import { ChallengeScore } from './ChallengeScore';
import { LearningCard } from './LearningCard';
import styles from './ChallengeCard.module.css';
import ChallengeCardMobile from '@/assets/images/dashboard/stan-gary-challenge-card-mobile.png';
import ChallengeCardDesktop from '@/assets/images/dashboard/stan-gary-challenge-card-desktop.png';

interface ChallengeCardProps {
    hideChallengeButton?: boolean;
}

export function ChallengeCard({ hideChallengeButton = false }: ChallengeCardProps) {
    const { user } = useUser();
    const { communityMagicLink, challengeKeyDate, challengeText } = useChallenge();
    const [challengeCommunityButtonLoading, setChallengeCommunityButtonLoading] = useState(false);

    const challengeStarted = useMemo(() => {
        if (!challengeKeyDate) return false;
        return new Date() > new Date(challengeKeyDate);
    }, [challengeKeyDate]);

    const notVisitedCommunity = useMemo(() => {
        return !user?.tags?.onboarding_referral_visit_community;
    }, [user?.tags]);

    const showChallengeButton = useMemo(() => {
        return !!communityMagicLink && !hideChallengeButton;
    }, [communityMagicLink, hideChallengeButton]);

    const learningMaterial = useMemo(
        () => ({
            preview_image_uri: 'https://i.ytimg.com/vi/Nj-AbC9B454/maxresdefault.jpg',
            video_uri: 'https://www.youtube.com/embed/Nj-AbC9B454?autoplay=1&rel=0',
        }),
        []
    );

    const goToChallengeCommunity = useCallback(async () => {
        if (!communityMagicLink || !user?.user_id) return;

        setChallengeCommunityButtonLoading(true);
        const payloadForTags = {
            reference_id: user.user_id,
            name: 'onboarding_referral_visit_community',
            type: 'user',
            data: {},
        };

        try {
            await mainApi.put('v1/tags', payloadForTags);
        } catch (error) {
            console.error('Error occurred while calling the API:', error);
        } finally {
            setChallengeCommunityButtonLoading(false);
        }

        window.open(communityMagicLink, '_self');
    }, [communityMagicLink, user?.user_id]);

    // Reusable button component to avoid repetition
    const ChallengeHubButton = useCallback(
        ({ className: additionalClassName }: { className?: string }) => (
            <StanButton
                className={cn(styles.ctaButton, 'w-full', additionalClassName)}
                color='dark'
                variant='square'
                size='xl'
                name='gary-vee-challenge-join-button'
                onClick={goToChallengeCommunity}
                loading={challengeCommunityButtonLoading}
            >
                Visit Challenge Hub
            </StanButton>
        ),
        [goToChallengeCommunity, challengeCommunityButtonLoading]
    );

    return (
        <div className={cn('flex flex-col', styles.container)}>
            {challengeStarted ? (
                <>
                    {showChallengeButton && (
                        <div className='flex w-full self-start pb-2 lg:hidden'>
                            <ChallengeHubButton />
                        </div>
                    )}
                    <div className={styles.challengeCard}>
                        <img
                            src={ChallengeCardMobile}
                            alt='Challenge Card'
                            className='block w-full lg:hidden'
                            style={{ maxWidth: '100%', height: 'auto' }}
                        />
                        <img
                            src={ChallengeCardDesktop}
                            alt='Challenge Card'
                            className='hidden w-full lg:block'
                            style={{ maxWidth: '100%', height: 'auto' }}
                        />
                        <ChallengeScore />
                    </div>
                    {showChallengeButton && (
                        <div className='hidden flex-col self-start pt-2 lg:flex'>
                            {notVisitedCommunity ? (
                                <>
                                    <h1 className={cn('pb-2', styles.challengeTitle)}>CHECK OUT THE HUB</h1>
                                    <h3 className={cn('mb-2 pb-2', styles.challengeSubtitle)}>
                                        Join the community and get your store ready.
                                    </h3>
                                    <div className='pt-1'>
                                        <ChallengeHubButton />
                                    </div>
                                </>
                            ) : (
                                <ChallengeHubButton />
                            )}
                        </div>
                    )}
                </>
            ) : (
                <>
                    {showChallengeButton && (
                        <div className='flex w-full self-start pb-2 lg:hidden'>
                            <ChallengeHubButton />
                        </div>
                    )}
                    <div className={styles.challengeCard}>
                        <div className={styles.challengeCountdown}>
                            {challengeText && <div className={styles.challengeEnds}>{challengeText}</div>}
                            {challengeKeyDate && (
                                <div className='flex items-center'>
                                    <ChallengeCountdown countdownTime={challengeKeyDate} />
                                </div>
                            )}
                        </div>
                        <LearningCard previewImageUri={learningMaterial.preview_image_uri} showPlay />
                    </div>
                    {showChallengeButton && (
                        <div className='hidden flex-col self-start pt-2 lg:flex'>
                            {notVisitedCommunity ? (
                                <>
                                    <h1 className={cn('pb-2', styles.challengeTitle)}>CHECK OUT THE HUB</h1>
                                    <h3 className={cn('mb-2 pb-2', styles.challengeSubtitle)}>
                                        Join the community and get your store ready.
                                    </h3>
                                    <div className='pt-1'>
                                        <ChallengeHubButton />
                                    </div>
                                </>
                            ) : (
                                <ChallengeHubButton />
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
